import type { ProductDealLeg } from "adl-gen/ferovinum/app/api";
import type { NumberOfUnits } from "adl-gen/ferovinum/app/db";
import type { FSP } from "adl-gen/ferovinum/app/views";
import { toNumber } from "lodash";
import { useCallback, useMemo } from "react";
import { add, isSingles } from "utils/model-utils";

interface ProductRowViewModel {
  totalUnsoldQuantity: NumberOfUnits;
  fspTransformation: (fspPerUnit: number) => number;
  deposits: number[];
  accessor: (val: FSP) => number;
}

type AtLeastOne<T> = [T, ...T[]];

const zero = (ns: AtLeastOne<NumberOfUnits>): NumberOfUnits => ({
  kind: ns[0].kind,
  value: 0,
});

const total = (ns: AtLeastOne<NumberOfUnits>): NumberOfUnits =>
  ns.reduce((sum, n) => add(sum, n), zero(ns));

const accessor = (fsp: FSP) => Number(fsp.roundedFsp);

const count = (n: NumberOfUnits): number => (isSingles(n) ? 1 : n.value);

export const useProductRowViewModel = (
  productDealLegs: ProductDealLeg[],
): ProductRowViewModel => {
  //TODO(Berto): Should come from the API as NumberOfUnits
  const totalUnsoldQuantity: NumberOfUnits = useMemo(
    () =>
      total(
        productDealLegs.map(
          (dl) => dl.totalUnsoldQuantity,
        ) as AtLeastOne<NumberOfUnits>,
      ),
    [productDealLegs],
  );

  const fspTransformation = useCallback(
    (fspPerUnit: number) => fspPerUnit * count(totalUnsoldQuantity),
    [totalUnsoldQuantity],
  );

  const deposits = useMemo(
    () =>
      productDealLegs.map(
        ({
          dealLegWithDbId: {
            value: { depositPricePerUnit, numberOfUnits },
          },
        }) => toNumber(depositPricePerUnit) * count(numberOfUnits),
      ),
    [productDealLegs],
  );

  return {
    totalUnsoldQuantity,
    fspTransformation,
    deposits,
    accessor,
  };
};
